import { QueryClient, useQuery } from '@tanstack/react-query';

import { getClassAvailabilities } from '@/services/class';

// Spots change while a page sits in the background, and a Home Screen app can
// stay open on one for days, so the grid is read again when the visitor comes
// back to it.
export const AVAILABILITY_STALE_MS = 60_000;

export type SubstituteInstructor = {
  first_name: string;
  last_name: string;
  profile_picture: string | null;
};

export type Slot = {
  slot_id: string | null;
  override_slot_id: string | null;
  start_time: string;
  end_time: string;
  timezone: string;
  available_spots: number | null;
  total_spots: number | null;
  is_fully_booked?: boolean;
  is_substituted?: boolean;
  original_instructor?: SubstituteInstructor | null;
  waitlist_enabled?: boolean;
  waitlist_count?: number;
};

export type Instructor = {
  admin_user_id: string;
  first_name: string;
  last_name: string;
  profile_picture: string | null;
  instructor_schedule_id: string;
  slots: Slot[];
};

export type Day = {
  date: Date | string;
  instructors: Instructor[];
};

export type ClassAvailabilities = {
  class: {
    class_id: string;
    name: string;
    description: string | null;
    price: string | null;
    currency: string;
    image: string | null;
    slug: string;
  };
  days: Day[];
};

export default function useClassAvailabilities({
  slug,
  start_date,
  end_date,
  timezone,
  enabled = true,
}: {
  slug: string;
  start_date: string;
  end_date: string;
  timezone: string;
  enabled?: boolean;
}) {
  const { data, isLoading, isFetching, isPlaceholderData, error, refetch } = useQuery({
    queryKey: ['class_availabilities', slug, start_date, end_date, timezone],
    queryFn: () =>
      getClassAvailabilities({ slug, start_date, end_date, timezone }),
    enabled,
    placeholderData: (prev) => prev,
    staleTime: AVAILABILITY_STALE_MS,
    refetchOnWindowFocus: true,
  });

  return {
    data,
    isLoading,
    isFetching,
    isPlaceholderData,
    error,
    refetch,
  };
}

// Removed rather than invalidated: invalidation refetches only queries a
// mounted page reads, and refetchOnMount is off, so the class page would still
// open on the old grid.
export function forgetClassAvailabilities(queryClient: QueryClient): void {
  queryClient.removeQueries({ queryKey: ['class_availabilities'] });
}
